import { Button, Menu, MenuContent, MenuList, MenuItem, Badge, Checkbox } from '@patternfly/react-core'
import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { CaretDownIcon, CaretUpIcon, FilterIcon, TimesIcon } from '@patternfly/react-icons'
import { useTranslation } from 'react-i18next';

import { userSelectors, modelFiltersSelectOpened, modelFiltersSelectToggled } from '../../store/user/userSlice';
import { Modality, ModalityDirection, Model, ModelType } from '../../store/global-types/customization-types';
import { StoreState } from '../../store/store';
import { getVisibilityStates, getModelVisibilityFilterAction, ModelFilter } from '../../store/global-types/filter-helpers';
import { contouringSelectors, activeTagFilterToggled as contourToggleActiveTagFilter, activeTagFiltersReset as contourActiveTagFiltersReset, activeModalityFilterToggled as contourActiveModalityFilterToggled, activeModalityFiltersReset as contourActiveModalityFiltersReset, activeVersionTagFilterToggled as contourActiveVersionTagFilter, activeVersionTagFiltersReset as contourActiveVersionTagFiltersReset } from '../../store/contouring/contouringSlice';
import { doseSelectors, activeTagFilterToggled as doseToggleActiveTagFilter, activeTagFiltersReset as doseActiveTagFiltersReset, activeModalityFilterToggled as doseActiveModalityFilterToggled, activeModalityFiltersReset as doseActiveModalityFiltersReset, activeVersionTagFilterToggled as doseActiveVersionTagFilter, activeVersionTagFiltersReset as doseActiveVersionTagFiltersReset } from '../../store/dose/doseSlice';
import { imageSelectors, activeTagFilterToggled as imageToggleActiveTagFilter, activeTagFiltersReset as imageActiveTagFiltersReset, activeModalityFilterToggled as imageActiveModalityFilterToggled, activeModalityFiltersReset as imageActiveModalityFiltersReset, activeVersionTagFilterToggled as imageActiveVersionTagFilter, activeVersionTagFiltersReset as imageActiveVersionTagFiltersReset } from '../../store/image/imageSlice';
import { adaptSelectors, activeTagFilterToggled as adaptToggleActiveTagFilter, activeTagFiltersReset as adaptActiveTagFiltersReset, activeModalityFilterToggled as adaptActiveModalityFilterToggled, activeModalityFiltersReset as adaptActiveModalityFiltersReset, activeVersionTagFilterToggled as adaptActiveVersionTagFilter, activeVersionTagFiltersReset as adaptActiveVersionTagFiltersReset } from '../../store/adapt/adaptSlice';
import { contourCommonSelectors } from '../../store/contouring/contouringSelectors';
import CustomizationFilterBadgeToggle from './CustomizationFilterBadgeToggle';
import { doseCommonSelectors } from '../../store/dose/doseSelectors';
import { imageCommonSelectors } from '../../store/image/imageSelectors';
import { adaptCommonSelectors } from '../../store/adapt/adaptSelectors';
import ModelModalities from './ModelModalities';
import TooltipWrapper from '../../components/tooltip-wrapper';

interface CustomizationFiltersProps {
    modelType: ModelType.Contouring | ModelType.Dose | ModelType.Image | ModelType.Adapt,
    isInline?: boolean,
    hideInputModalityFilter?: boolean,
}

const CustomizationFilters = (props: CustomizationFiltersProps) => {
    const { modelType, isInline = false, hideInputModalityFilter = false } = props;

    const dispatch = useDispatch();
    const { t } = useTranslation();

    // collect models matching current module (model type)

    let deprecatedModels: Model[];
    let nonLicensedModels: Model[];
    let hiddenModels: Model[];

    // useSelector hooks must be always run in the same order so we have to move them
    // outside of the switch statement below
    const contourDeprecatedModels = useSelector(contourCommonSelectors.selectDeprecatedModels);
    const contourNonLicensedModels = useSelector(contourCommonSelectors.selectNonLicensedModels);
    const contourHiddenModels = useSelector(contourCommonSelectors.selectUserHiddenModels);
    const doseDeprecatedModels = useSelector(doseCommonSelectors.selectDeprecatedModels);
    const doseNonLicensedModels = useSelector(doseCommonSelectors.selectNonLicensedModels);
    const doseHiddenModels = useSelector(doseCommonSelectors.selectUserHiddenModels);
    const imageDeprecatedModels = useSelector(imageCommonSelectors.selectDeprecatedModels);
    const imageNonLicensedModels = useSelector(imageCommonSelectors.selectNonLicensedModels);
    const imageHiddenModels = useSelector(imageCommonSelectors.selectUserHiddenModels);
    const adaptDeprecatedModels = useSelector(adaptCommonSelectors.selectDeprecatedModels);
    const adaptNonLicensedModels = useSelector(adaptCommonSelectors.selectNonLicensedModels);
    const adaptHiddenModels = useSelector(adaptCommonSelectors.selectUserHiddenModels);

    switch (modelType) {
        case ModelType.Contouring:
            deprecatedModels = contourDeprecatedModels;
            nonLicensedModels = contourNonLicensedModels;
            hiddenModels = contourHiddenModels;
            break;
        case ModelType.Dose:
            deprecatedModels = doseDeprecatedModels;
            nonLicensedModels = doseNonLicensedModels;
            hiddenModels = doseHiddenModels;
            break;
        case ModelType.Image:
            deprecatedModels = imageDeprecatedModels;
            nonLicensedModels = imageNonLicensedModels;
            hiddenModels = imageHiddenModels;
            break;
        case ModelType.Adapt:
            deprecatedModels = adaptDeprecatedModels;
            nonLicensedModels = adaptNonLicensedModels;
            hiddenModels = adaptHiddenModels;
            break;
        default:
            throw new Error('Unsupported model type');
    }

    // collect modalities matching current module (model type)

    let allInputModalities: Modality[] = [];
    let allOutputModalities: Modality[] = [];
    let allActiveInputModalities: Modality[] = [];
    let allActiveOutputModalities: Modality[] = [];

    const allContourInputModalities = useSelector(contourCommonSelectors.selectInputModalities);
    const allContourOutputModalities = useSelector(contourCommonSelectors.selectOutputModalities);
    const allActiveContourInputModalities = useSelector(contouringSelectors.selectActiveInputModalities);
    const allActiveContourOutputModalities = useSelector(contouringSelectors.selectActiveOutputModalities);

    const allDoseInputModalities = useSelector(doseCommonSelectors.selectInputModalities);
    const allDoseOutputModalities = useSelector(doseCommonSelectors.selectOutputModalities);
    const allActiveDoseInputModalities = useSelector(doseSelectors.selectActiveInputModalities);
    const allActiveDoseOutputModalities = useSelector(doseSelectors.selectActiveOutputModalities);

    const allImageInputModalities = useSelector(imageCommonSelectors.selectInputModalities);
    const allImageOutputModalities = useSelector(imageCommonSelectors.selectOutputModalities);
    const allActiveImageInputModalities = useSelector(imageSelectors.selectActiveInputModalities);
    const allActiveImageOutputModalities = useSelector(imageSelectors.selectActiveOutputModalities);

    const allAdaptInputModalities = useSelector(adaptCommonSelectors.selectInputModalities);
    const allAdaptOutputModalities = useSelector(adaptCommonSelectors.selectOutputModalities);
    const allActiveAdaptInputModalities = useSelector(adaptSelectors.selectActiveInputModalities);
    const allActiveAdaptOutputModalities = useSelector(adaptSelectors.selectActiveOutputModalities);

    switch (modelType) {
        case ModelType.Contouring:
            allInputModalities = allContourInputModalities;
            allOutputModalities = allContourOutputModalities;
            allActiveInputModalities = allActiveContourInputModalities;
            allActiveOutputModalities = allActiveContourOutputModalities;
            break;
        case ModelType.Dose:
            allInputModalities = allDoseInputModalities;
            allOutputModalities = allDoseOutputModalities;
            allActiveInputModalities = allActiveDoseInputModalities;
            allActiveOutputModalities = allActiveDoseOutputModalities;
            break;
        case ModelType.Image:
            allInputModalities = allImageInputModalities;
            allOutputModalities = allImageOutputModalities;
            allActiveInputModalities = allActiveImageInputModalities;
            allActiveOutputModalities = allActiveImageOutputModalities;
            break;
        case ModelType.Adapt:
            allInputModalities = allAdaptInputModalities;
            allOutputModalities = allAdaptOutputModalities;
            allActiveInputModalities = allActiveAdaptInputModalities;
            allActiveOutputModalities = allActiveAdaptOutputModalities;
            break;
        default:
            throw new Error('Unsupported model type');
    }

    // collect tags matching current module (model type)

    let allTags: string[] = [];
    let allActiveTags: string[] = [];
    let allVersionTags: string[] = [];
    let allActiveVersionTags: string[] = [];

    const allContourTags = useSelector(contourCommonSelectors.selectAllTagsInOrder);
    const allActiveContourTags = useSelector(contouringSelectors.selectActiveTags);
    const allContourVersionTags = useSelector(contourCommonSelectors.selectAllVersionTagsInOrder);
    const allActiveContourVersionTags = useSelector(contouringSelectors.selectActiveVersionTags);
    const allDoseTags = useSelector(doseCommonSelectors.selectAllTagsInOrder);
    const allActiveDoseTags = useSelector(doseSelectors.selectActiveTags);
    const allDoseVersionTags = useSelector(doseCommonSelectors.selectAllVersionTagsInOrder);
    const allActiveDoseVersionTags = useSelector(doseSelectors.selectActiveVersionTags);
    const allImageTags = useSelector(imageCommonSelectors.selectAllTagsInOrder);
    const allActiveImageTags = useSelector(imageSelectors.selectActiveTags);
    const allImageVersionTags = useSelector(imageCommonSelectors.selectAllVersionTagsInOrder);
    const allActiveImageVersionTags = useSelector(imageSelectors.selectActiveVersionTags);
    const allAdaptTags = useSelector(adaptCommonSelectors.selectAllTagsInOrder);
    const allActiveAdaptTags = useSelector(adaptSelectors.selectActiveTags);
    const allAdaptVersionTags = useSelector(adaptCommonSelectors.selectAllVersionTagsInOrder);
    const allActiveAdaptVersionTags = useSelector(adaptSelectors.selectActiveVersionTags);

    switch (modelType) {
        case ModelType.Contouring:
            allTags = allContourTags;
            allActiveTags = allActiveContourTags;
            allVersionTags = allContourVersionTags;
            allActiveVersionTags = allActiveContourVersionTags;
            break;
        case ModelType.Dose:
            allTags = allDoseTags;
            allActiveTags = allActiveDoseTags;
            allVersionTags = allDoseVersionTags;
            allActiveVersionTags = allActiveDoseVersionTags;
            break;
        case ModelType.Image:
            allTags = allImageTags;
            allActiveTags = allActiveImageTags;
            allVersionTags = allImageVersionTags;
            allActiveVersionTags = allActiveImageVersionTags;
            break;
        case ModelType.Adapt:
            allTags = allAdaptTags;
            allActiveTags = allActiveAdaptTags;
            allVersionTags = allAdaptVersionTags;
            allActiveVersionTags = allActiveAdaptVersionTags;
            break;
        default:
            throw new Error('Unsupported model type');
    }

    // Get model visibility state from the appropriate slice based on model type
    const { areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown } = useSelector(
        (state: StoreState) => getVisibilityStates(state, modelType)
    );
    
    const isModelFiltersSelectOpen = useSelector(userSelectors.selectIsModelFiltersSelectOpen);

    const handleTagBadgeToggled = useCallback((label: string) => {
        switch (modelType) {
            case ModelType.Contouring:
                dispatch(contourToggleActiveTagFilter(label));
                break;
            case ModelType.Dose:
                dispatch(doseToggleActiveTagFilter(label));
                break;
            case ModelType.Image:
                dispatch(imageToggleActiveTagFilter(label));
                break;
            case ModelType.Adapt:
                dispatch(adaptToggleActiveTagFilter(label));
                break;
            default:
                throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [dispatch, modelType]);

    const handleVersionTagBadgeToggled = useCallback((label: string) => {
        switch (modelType) {
            case ModelType.Contouring:
                dispatch(contourActiveVersionTagFilter(label));
                break;
            case ModelType.Dose:
                dispatch(doseActiveVersionTagFilter(label));
                break;
            case ModelType.Image:
                dispatch(imageActiveVersionTagFilter(label));
                break;
            case ModelType.Adapt:
                dispatch(adaptActiveVersionTagFilter(label));
                break;
            default:
                throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [dispatch, modelType]);
    
    const handleClearActiveVersionTagsClicked = useCallback(() => {
        switch (modelType) {
            case ModelType.Contouring:
                dispatch(contourActiveVersionTagFiltersReset());
                break;
            case ModelType.Dose:
                dispatch(doseActiveVersionTagFiltersReset());
                break;
            case ModelType.Image:
                dispatch(imageActiveVersionTagFiltersReset());
                break;
            case ModelType.Adapt:
                dispatch(adaptActiveVersionTagFiltersReset());
                break;
            default:
                throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [dispatch, modelType]);

    const handleClearActiveTagsClicked = useCallback(() => {
        switch (modelType) {
            case ModelType.Contouring:
                dispatch(contourActiveTagFiltersReset());
                break;
            case ModelType.Dose:
                dispatch(doseActiveTagFiltersReset());
                break;
            case ModelType.Image:
                dispatch(imageActiveTagFiltersReset());
                break;
            case ModelType.Adapt:
                dispatch(adaptActiveTagFiltersReset());
                break;
            default:
                throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [dispatch, modelType]);

    const handleModalityToggled = useCallback((direction: ModalityDirection, modality: Modality) => {
        switch (modelType) {
            case ModelType.Contouring:
                dispatch(contourActiveModalityFilterToggled({ direction, modality }));
                break;
            case ModelType.Dose:
                dispatch(doseActiveModalityFilterToggled({ direction, modality }));
                break;
            case ModelType.Image:
                dispatch(imageActiveModalityFilterToggled({ direction, modality }));
                break;
            case ModelType.Adapt:
                dispatch(adaptActiveModalityFilterToggled({ direction, modality }));
                break;
            default:
                throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [dispatch, modelType]);

    const handleModalityReset = useCallback((direction: ModalityDirection) => {
        switch (modelType) {
            case ModelType.Contouring:
                dispatch(contourActiveModalityFiltersReset(direction));
                break;
            case ModelType.Dose:
                dispatch(doseActiveModalityFiltersReset(direction));
                break;
            case ModelType.Image:
                dispatch(imageActiveModalityFiltersReset(direction));
                break;
            case ModelType.Adapt:
                dispatch(adaptActiveModalityFiltersReset(direction));
                break;
            default:
                throw new Error(`Unsupported model type: ${modelType}`);
        }
    }, [dispatch, modelType]);

    const handleModelsVisibilityChange = useCallback((type: ModelFilter) => {
        let isCurrentlyShown: boolean;
        switch (type) {
            case ModelFilter.Deprecated:
                isCurrentlyShown = areDeprecatedModelsShown;
                break;
            case ModelFilter.NonLicensed:
                isCurrentlyShown = areNonLicensedModelsShown;
                break;
            case ModelFilter.UserHidden:
                isCurrentlyShown = areUserHiddenModelsShown;
                break;
            default:
                throw new Error(`Unsupported model filter type: ${type}`);
        }

        const newValue = !isCurrentlyShown;
        dispatch(getModelVisibilityFilterAction(modelType, type)(newValue));
    }, [dispatch, modelType, areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown]);

    const numberOfActiveFilters = useMemo(() => {
        let numberOfFilters = allActiveTags.length;
        numberOfFilters += allActiveVersionTags.length;
        numberOfFilters += allActiveInputModalities.length;
        numberOfFilters += allActiveOutputModalities.length;
        if (areDeprecatedModelsShown) { numberOfFilters++; }
        if (areNonLicensedModelsShown) { numberOfFilters++; }
        if (areUserHiddenModelsShown) { numberOfFilters++; }
        return numberOfFilters;
    }, [allActiveInputModalities.length, allActiveOutputModalities.length, allActiveTags.length, allActiveVersionTags.length, areDeprecatedModelsShown, areNonLicensedModelsShown, areUserHiddenModelsShown]);

    const filterExpandCollapseIcon = useMemo(() => {
        return (isModelFiltersSelectOpen) ? <CaretUpIcon className="models-visibility-icon" /> : <CaretDownIcon className="models-visibility-icon" />;
    }, [isModelFiltersSelectOpen]);

    const filterActivityBadge = useMemo(() => {
        if (numberOfActiveFilters > 0) {
            return (<Badge className="filter-models-badge">{numberOfActiveFilters}</Badge>);
        } else {
            return null;
        }
    }, [numberOfActiveFilters]);

    const handleToggleModelFiltersSelectOpen = useCallback(() => {
        dispatch(modelFiltersSelectToggled());
    }, [dispatch]);

    // Auto-open logic with awareness of previous count and current open state
    // - Open when filters increase (user enables a filter) and menu is closed
    // - If menu is closed and user disables a filter but others remain (>0), open to show remaining active filters
    // - Never auto-close here (closing is a deliberate user action)
    const prevActiveCountRef = React.useRef<number | null>(null);
    const prevModelTypeRef = React.useRef<ModelType | null>(null);

    useEffect(() => {
        const prev = prevActiveCountRef.current;
        const curr = numberOfActiveFilters;

        // Reset baseline when model type changes (e.g., navigating between pages)
        if (prevModelTypeRef.current !== null && prevModelTypeRef.current !== modelType) {
            prevActiveCountRef.current = curr;
            prevModelTypeRef.current = modelType;
            return;
        }
        prevModelTypeRef.current = modelType;

        // Skip the first render to establish the baseline
        if (prev === null) {
            prevActiveCountRef.current = curr;
            return;
        }

        const isIncreasing = curr > prev && !isModelFiltersSelectOpen;
        const decreasedButStillActive = prev > curr && curr > 0 && !isModelFiltersSelectOpen;


        if (isIncreasing || decreasedButStillActive) {
            dispatch(modelFiltersSelectOpened());
        }

        prevActiveCountRef.current = curr;
    }, [numberOfActiveFilters, isModelFiltersSelectOpen, dispatch, modelType]);

    
    // Shared filter sections (used in both inline and menu modes)

    const inputModalitiesSection = allInputModalities.length > 0 && (
        <div data-testid="model-filter-input-modalities">
            <div className='models-filter-modalities-area'>
                {allActiveInputModalities.length > 0 && (
                    <div className="clear-modalities-button">
                        <Button variant="plain" isInline onClick={() => handleModalityReset(ModalityDirection.Input)} data-testid="model-filter-input-modalities-clear"><TimesIcon /></Button>
                    </div>
                )}
                <ModelModalities
                    label={t('filterModels.inputModalities')}
                    modalities={allInputModalities}
                    direction={ModalityDirection.Input}
                    selectedModalities={allActiveInputModalities}
                    onToggle={(modality) => handleModalityToggled(ModalityDirection.Input, modality)}
                />
            </div>
        </div>
    );

    const outputModalitiesSection = allOutputModalities.length > 0 && (
        <div data-testid="model-filter-output-modalities">
            <div className='models-filter-modalities-area'>
                {allActiveOutputModalities.length > 0 && (
                    <div className="clear-modalities-button">
                        <Button variant="plain" isInline onClick={() => handleModalityReset(ModalityDirection.Output)} data-testid="model-filter-output-modalities-clear"><TimesIcon /></Button>
                    </div>
                )}
                <ModelModalities
                    label={t('filterModels.outputModalities')}
                    modalities={allOutputModalities}
                    direction={ModalityDirection.Output}
                    selectedModalities={allActiveOutputModalities}
                    onToggle={(modality) => handleModalityToggled(ModalityDirection.Output, modality)}
                />
            </div>
        </div>
    );

    const tagsSection = allTags.length > 0 && (
        <div className="models-filter-tags-area" data-testid="model-filter-tags">
            <span className='models-filter-tags-title'>{t('filterModels.tags')}:</span>
            <div className='models-filter-tags-container'>
                {allTags.map(tag => (
                    <span key={tag} className='models-filter-tag'>
                        <CustomizationFilterBadgeToggle
                            label={tag}
                            allActiveTags={allActiveTags}
                            onToggle={handleTagBadgeToggled}
                            testId="model-filter-tag-badge"
                        />
                    </span>
                ))}
                {allActiveTags.length > 0 && (
                    <span><Button variant="plain" className="clear-tags-button" onClick={handleClearActiveTagsClicked} data-testid="model-filter-tags-clear">Clear</Button></span>
                )}
            </div>
        </div>
    );

    const versionTagsSection = allVersionTags.length > 0 && (
        <div className="models-filter-tags-area" data-testid="model-filter-version-tags">
            <TooltipWrapper tooltipContent={t('filterModels.versionTagsTooltip')}>
                <span className='models-filter-tags-title'>{t('filterModels.versionTags')}:</span>
            </TooltipWrapper>
            <div className='models-filter-tags-container'>
                {allVersionTags.map(tag => (
                    <span key={tag} className='models-filter-tag'>
                        <CustomizationFilterBadgeToggle
                            label={tag}
                            allActiveTags={allActiveVersionTags}
                            onToggle={handleVersionTagBadgeToggled}
                            testId="model-filter-version-tag-badge"
                        />
                    </span>
                ))}
                {allActiveVersionTags.length > 0 && (
                    <span><Button variant="plain" className="clear-tags-button" onClick={handleClearActiveVersionTagsClicked} data-testid="model-filter-version-tags-clear">{t('filterModels.clear')}</Button></span>
                )}
            </div>
        </div>
    );

    // Inline rendering mode
    if (isInline) {
        return (
            <div className="customization-filters-inline" data-testid="model-filter-panel">
                <div className='models-filter-visibility-area'>
                    <Checkbox
                        id="deprecated-models-checkbox"
                        label={`${t('filterModels.showDeprecated')}${deprecatedModels.length > 0 ? ` (${deprecatedModels.length})` : ''}`}
                        isChecked={areDeprecatedModelsShown}
                        onChange={() => handleModelsVisibilityChange(ModelFilter.Deprecated)}
                    />
                    <Checkbox
                        id="non-licensed-models-checkbox"
                        label={`${t('filterModels.showUnavailable')}${nonLicensedModels.length > 0 ? ` (${nonLicensedModels.length})` : ''}`}
                        isChecked={areNonLicensedModelsShown}
                        onChange={() => handleModelsVisibilityChange(ModelFilter.NonLicensed)}
                    />
                    <Checkbox
                        id="user-hidden-models-checkbox"
                        label={`${t('filterModels.showHidden')}${hiddenModels.length > 0 ? ` (${hiddenModels.length})` : ''}`}
                        isChecked={areUserHiddenModelsShown}
                        onChange={() => handleModelsVisibilityChange(ModelFilter.UserHidden)}
                    />
                </div>
                {!hideInputModalityFilter && inputModalitiesSection}
                {outputModalitiesSection}
                {tagsSection}
                {versionTagsSection}
            </div>
        );
    }

    // Menu rendering mode (default)
    return (
        <>
            <Button variant="secondary" className='filter-models-toggle' onClick={handleToggleModelFiltersSelectOpen} data-testid="model-filter-toggle"><span className="filter-models-title"><FilterIcon width={12}/>{t('filterModels.title')}{filterActivityBadge}<span className="filter-models-caret">{filterExpandCollapseIcon}</span></span>
            </Button >
            {isModelFiltersSelectOpen &&
                <Menu className="customization-filter-menu unselectable" data-testid="model-filter-panel">
                    <MenuContent>
                        <MenuList>
                            <MenuItem className='models-filter-menu-item' itemId={1} onClick={() => handleModelsVisibilityChange(ModelFilter.Deprecated)} hasCheckbox isSelected={areDeprecatedModelsShown} title={t('filterModels.showDeprecated')}>{t('filterModels.showDeprecated')}{deprecatedModels.length > 0 && ` (${deprecatedModels.length})`}</MenuItem>
                            <MenuItem className='models-filter-menu-item' itemId={2} onClick={() => handleModelsVisibilityChange(ModelFilter.NonLicensed)} hasCheckbox isSelected={areNonLicensedModelsShown} title={t('filterModels.showUnavailable')}>{t('filterModels.showUnavailable')}{nonLicensedModels.length > 0 && ` (${nonLicensedModels.length})`}</MenuItem>
                            <MenuItem className='models-filter-menu-item' itemId={3} onClick={() => handleModelsVisibilityChange(ModelFilter.UserHidden)} hasCheckbox isSelected={areUserHiddenModelsShown} title={t('filterModels.showHidden')}>{t('filterModels.showHidden')}{hiddenModels.length > 0 && ` (${hiddenModels.length})`}</MenuItem>
                        </MenuList>
                    </MenuContent>
                    {!hideInputModalityFilter && inputModalitiesSection && (
                        <MenuContent>
                            {inputModalitiesSection}
                        </MenuContent>
                    )}
                    {outputModalitiesSection && (
                        <MenuContent>
                            {outputModalitiesSection}
                        </MenuContent>
                    )}
                    {tagsSection && (
                        <MenuContent>
                            {tagsSection}
                        </MenuContent>
                    )}
                    {versionTagsSection && (
                        <MenuContent>
                            {versionTagsSection}
                        </MenuContent>
                    )}
                </Menu>
            }
        </>
    )
}

export default CustomizationFilters;
